/**
 * /garage/depots — les véhicules à vendre visibles d'un garage partenaire.
 *
 * CE QUE CETTE PAGE NE PEUT PAS AFFICHER
 * ─────────────────────────────────────────────────────────────────────────
 * Ni prénom, ni téléphone, ni email, ni code postal — non pas parce qu'on les
 * masque ici, mais parce que le serveur ne les envoie pas : le schéma servi
 * aux garages (`DepotGarage`) ne DÉCLARE pas ces champs. Chercher à les
 * afficher ne produirait pas une fuite, seulement `undefined`.
 *
 * Les coordonnées n'arrivent qu'en réponse à un déverrouillage abouti, et
 * elles coûtent un crédit. D'où la confirmation : un crédit dépensé ne se
 * reprend pas, et le vendeur est prévenu dans la foulée.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Page from '../components/Layout/Page';
import { nombre } from '../components/Affaires/format';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './GarageDepotsPage.css';

const ETATS = { tres_bon: 'Très bon état', bon: 'Bon état', frais: 'Frais à prévoir', hs: 'Ne roule pas' };
const NIVEAUX = {
  SOLIDE: 'cote solide', INDICATIVE: 'cote indicative',
  PERCENTILE: 'positionnement', ESTIMATION: 'estimation indicative',
};
const ANNEES = [2015, 2018, 2020, 2022];
const KMS = [60000, 100000, 150000, 200000];

const euros = (n) => (n == null ? '—' : `${nombre(n)} €`);
const depuis = (iso) => {
  if (!iso) return '';
  const j = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (j <= 0) return "Déposé aujourd'hui";
  return `Déposé il y a ${j} jour${j > 1 ? 's' : ''}`;
};
const message = (err, repli) => {
  const d = err.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (d?.message) return d.message;
  return repli;
};

function Coordonnees({ c }) {
  return (
    <div className="zk-gdp-coordonnees">
      <b>{c.prenom} · {c.code_postal}</b>
      <a href={`tel:${c.telephone}`}>{c.telephone}</a>
      <a href={`mailto:${c.email}`}>{c.email}</a>
      <small>
        {c.deja_deverrouille
          ? 'Coordonnées déjà débloquées — aucun crédit n’a été redébité.'
          : 'Le vendeur vient d’être prévenu que vous allez le contacter.'}
      </small>
    </div>
  );
}

function Depot({ depot, onDeverrouiller, occupe }) {
  const [confirme, setConfirme] = useState(false);
  const [coordonnees, setCoordonnees] = useState(null);
  const [erreur, setErreur] = useState(null);
  const e = depot.estimation || {};
  const photo = depot.photos?.[0]
    ? `/api/garage/depots/${depot.id}/photos/${depot.photos[0]}`
    : null;

  async function debloquer() {
    setErreur(null);
    try {
      setCoordonnees(await onDeverrouiller(depot));
      setConfirme(false);
    } catch (err) {
      setErreur(message(err, 'Le déverrouillage a échoué.'));
      setConfirme(false);
    }
  }

  return (
    <article className="zk-gdp-depot zk-carte">
      {photo
        ? <img className="zk-gdp-photo" src={photo} alt={`${depot.marque} ${depot.modele} ${depot.annee}`} loading="lazy" />
        : <div className="zk-gdp-photo vide" aria-hidden="true">Pas de photo</div>}

      <div className="zk-gdp-corps">
        <header>
          <h3>{depot.marque} {depot.modele}</h3>
          <span className="zk-gdp-secteur">{depot.zone_libelle || 'Secteur inconnu'}</span>
        </header>

        <p className="zk-gdp-specs">
          {[depot.annee, `${nombre(depot.kilometrage)} km`, ETATS[depot.etat]].filter(Boolean).join(' · ')}
        </p>

        <dl className="zk-gdp-chiffres">
          <div>
            <dt>Valeur entre particuliers</dt>
            <dd>{euros(e.prix_particulier)}<small>{NIVEAUX[e.niveau] || 'sans estimation'}</small></dd>
          </div>
          <div>
            <dt>Fourchette de reprise</dt>
            <dd>{euros(e.reprise_bas)} – {euros(e.reprise_haut)}</dd>
          </div>
          <div>
            <dt>Prix souhaité</dt>
            <dd>{euros(depot.prix_souhaite)}</dd>
          </div>
        </dl>

        {depot.description_publique && (
          <p className="zk-gdp-description">« {depot.description_publique} »</p>
        )}

        <p className="zk-gdp-date">{depuis(depot.publie_le)}</p>

        {coordonnees ? <Coordonnees c={coordonnees} /> : (
          <>
            {!confirme && (
              <button type="button" className="zk-bouton plein" disabled={occupe}
                      onClick={() => (depot.deja_deverrouille ? debloquer() : setConfirme(true))}>
                {depot.deja_deverrouille
                  ? 'Revoir les coordonnées (déjà payé)'
                  : 'Voir les coordonnées (1 crédit)'}
              </button>
            )}
            {confirme && (
              <div className="zk-gdp-confirmation">
                <p>
                  Un crédit sera débité et <b>le vendeur sera prévenu</b> que vous allez
                  le contacter. Cette action ne s’annule pas.
                </p>
                <div className="zk-gdp-actions">
                  <button type="button" className="zk-bouton" disabled={occupe} onClick={debloquer}>
                    Confirmer
                  </button>
                  <button type="button" className="zk-bouton second" disabled={occupe}
                          onClick={() => setConfirme(false)}>
                    Annuler
                  </button>
                </div>
              </div>
            )}
          </>
        )}
        {erreur && <p className="zk-gdp-erreur">{erreur}</p>}
      </div>
    </article>
  );
}

export default function GarageDepotsPage() {
  const { user } = useAuth();
  const [filtres, setFiltres] = useState({ marque: '', annee_min: '', km_max: '' });
  const [depots, setDepots] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [credits, setCredits] = useState(user?.credits_restants ?? null);
  const [chargement, setChargement] = useState(true);
  const [occupe, setOccupe] = useState(false);
  const [erreur, setErreur] = useState(null);

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    try {
      const params = { page };
      if (filtres.marque.trim()) params.marque = filtres.marque.trim();
      if (filtres.annee_min) params.annee_min = Number(filtres.annee_min);
      if (filtres.km_max) params.km_max = Number(filtres.km_max);
      const { data } = await api.get('/api/garage/depots', { params });
      setDepots(data.depots || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } catch (err) {
      setDepots([]);
      setErreur(message(err, 'Impossible de charger les véhicules à vendre.'));
    } finally {
      setChargement(false);
    }
  }, [filtres, page]);

  useEffect(() => { charger(); }, [charger]);
  useEffect(() => { if (user?.credits_restants != null) setCredits(user.credits_restants); }, [user]);

  async function deverrouiller(depot) {
    setOccupe(true);
    try {
      const { data } = await api.post(`/api/garage/depots/${depot.id}/offre`);
      if (data.credits_restants != null) setCredits(data.credits_restants);
      return data;
    } finally {
      setOccupe(false);
    }
  }

  const majFiltre = (k) => (e) => { setPage(1); setFiltres((f) => ({ ...f, [k]: e.target.value })); };
  const actifs = filtres.marque || filtres.annee_min || filtres.km_max;

  return (
    <Page
      titre="Véhicules à vendre près de chez vous"
      chapeau="Des particuliers qui veulent vendre, dans votre rayon. La fiche est anonyme tant que vous n’avez pas demandé les coordonnées."
      largeur="large"
    >
      <div className="zk-gdp-tete">
        <p className="zk-gdp-solde">
          {credits != null
            ? <>Crédits restants : <b>{credits}</b></>
            : <>Un crédit par vendeur déverrouillé.</>}
        </p>
        <p className="zk-gdp-compte">
          {chargement ? 'Chargement…' : `${total} véhicule${total > 1 ? 's' : ''} dans votre rayon`}
        </p>
      </div>

      <div className="zk-gdp-filtres">
        <label className="zk-gdp-champ">
          <span>Marque</span>
          <input value={filtres.marque} onChange={majFiltre('marque')} placeholder="Toutes" />
        </label>
        <label className="zk-gdp-champ">
          <span>À partir de</span>
          <select value={filtres.annee_min} onChange={majFiltre('annee_min')}>
            <option value="">Toutes années</option>
            {ANNEES.map((a) => <option key={a} value={a}>{a} et après</option>)}
          </select>
        </label>
        <label className="zk-gdp-champ">
          <span>Kilométrage</span>
          <select value={filtres.km_max} onChange={majFiltre('km_max')}>
            <option value="">Tous</option>
            {KMS.map((k) => <option key={k} value={k}>moins de {nombre(k)} km</option>)}
          </select>
        </label>
        {actifs && (
          <button type="button" className="zk-bouton second"
                  onClick={() => { setPage(1); setFiltres({ marque: '', annee_min: '', km_max: '' }); }}>
            Effacer
          </button>
        )}
      </div>

      {erreur && <p className="zk-alerte">{erreur}</p>}

      {!chargement && depots.length === 0 && !erreur && (
        <div className="zk-carte zk-gdp-vide">
          <p><b>Aucun véhicule à vendre dans votre rayon pour l’instant.</b></p>
          <p>
            Les dépôts sont qualifiés à la main avant d’être publiés, puis visibles 30 jours.
            Élargir votre rayon d’intervention se fait depuis votre <Link className="zk-lien" to="/profile">profil</Link>.
          </p>
        </div>
      )}

      <div className="zk-gdp-liste">
        {depots.map((d) => (
          <Depot key={d.id} depot={d} occupe={occupe} onDeverrouiller={deverrouiller} />
        ))}
      </div>

      {pages > 1 && (
        <div className="zk-gdp-pagination">
          <button type="button" className="zk-bouton second" disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}>Précédent</button>
          <span>Page {page} sur {pages}</span>
          <button type="button" className="zk-bouton second" disabled={page >= pages}
                  onClick={() => setPage((p) => p + 1)}>Suivant</button>
        </div>
      )}
    </Page>
  );
}
